import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, SkeletonTable } from '../../components/ui';
import EmptyState from '../../components/ui/EmptyState';
import HelpTooltip from '../../components/ui/HelpTooltip';
import { translateError } from '../../utils/errorMessages';

interface IGroup {
  _id: string;
  name: string;
}

interface IMembership {
  _id: string;
  name: string;
  description: string;
  type?: 'season' | 'calendar' | 'time' | 'entry';
  yearlyPrice: number;
  entryCount?: number | null;
  prices?: Array<{ durationDays: number; price: number; label: string; sortOrder: number }>;
  calendarConfig?: { startMonth: number; intervals: Partial<Record<'yearly' | 'half_year' | 'quarter' | 'monthly', number>> };
  /** Season-division: how many seasons this reusable membership is offered in. */
  usedBySeasonCount?: number;
  groups: IGroup[];
}

const MembershipsListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [memberships, setMemberships] = useState<IMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  // Permission checks
  const canCreate = hasPermission('memberships:write');
  const canEdit = hasPermission('memberships:write');
  const canDelete = hasPermission('memberships:delete');

  const fetchMemberships = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.memberships.list();
      setMemberships(data as IMembership[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const handleDeleteClick = (membership: IMembership) => {
    if (!canDelete) return;
    setDeleteId(membership._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await api.memberships.delete(deleteId);
      fetchMemberships();
      addToast(t('memberships.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'memberships.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const columns: Column<IMembership>[] = useMemo(
    () => {
      const intervalKeys = ['yearly', 'half_year', 'quarter', 'monthly'] as const;
      const intervalI18n: Record<string, string> = {
        yearly: t('memberships.intervalYearly'),
        half_year: t('memberships.intervalHalfYear'),
        quarter: t('memberships.intervalQuarter'),
        monthly: t('memberships.intervalMonthly'),
      };

      const renderPrice = (membership: IMembership) => {
        if (membership.type === 'calendar' && membership.calendarConfig?.intervals) {
          const intervals = membership.calendarConfig.intervals;
          const entries = intervalKeys
            .filter((k) => intervals[k] != null)
            .map((k) => ({ label: intervalI18n[k], price: intervals[k]! }));
          if (entries.length === 0) return (membership.yearlyPrice ?? 0).toFixed(2);
          const maxPrice = Math.max(...entries.map((e) => e.price));
          const tooltip = entries.map((e) => `${e.label}: ${e.price.toFixed(2)}`).join(' | ');
          return (
            <HelpTooltip helpText={tooltip}>
              {maxPrice.toFixed(2)}
            </HelpTooltip>
          );
        }

        if (membership.type === 'time' && membership.prices?.length) {
          const maxPrice = Math.max(...membership.prices.map((p) => p.price));
          const tooltip = membership.prices
            .slice()
            .sort((a, b) => b.durationDays - a.durationDays)
            .map((p) => `${p.label || `${p.durationDays}d`}: ${p.price.toFixed(2)}`)
            .join(' | ');
          return (
            <HelpTooltip helpText={tooltip}>
              {maxPrice.toFixed(2)}
            </HelpTooltip>
          );
        }

        if (membership.type === 'entry') {
          const tooltip = membership.entryCount
            ? `${membership.entryCount} ${t('memberships.entryCount').toLowerCase()}`
            : '';
          return tooltip ? (
            <HelpTooltip helpText={tooltip}>
              {(membership.yearlyPrice ?? 0).toFixed(2)}
            </HelpTooltip>
          ) : (membership.yearlyPrice ?? 0).toFixed(2);
        }

        return (membership.yearlyPrice ?? 0).toFixed(2);
      };

      const typeLabels: Record<string, string> = {
        season: t('memberships.typeSeason'),
        calendar: t('memberships.typeCalendar'),
        time: t('memberships.typeTime'),
        entry: t('memberships.typeEntry'),
      };

      return [
        {
          header: t('common.name'),
          key: 'name' as const,
          sortValue: (membership: IMembership) => membership.name ?? null,
        },
        {
          header: t('memberships.type'),
          render: (membership: IMembership) =>
            membership.type ? (typeLabels[membership.type] || '—') : '—',
          sortValue: (membership: IMembership) =>
            membership.type ? (typeLabels[membership.type] || null) : null,
        },
        {
          header: t('memberships.price'),
          render: renderPrice,
          sortValue: (membership: IMembership) => membership.yearlyPrice ?? 0,
        },
        {
          header: t('memberships.seasonsCount'),
          render: (membership: IMembership) => membership.usedBySeasonCount ?? 0,
          sortValue: (membership: IMembership) => membership.usedBySeasonCount ?? 0,
        },
      ];
    },
    [t]
  );

  const filteredMemberships = useMemo(() => applyFilters(memberships, filters), [memberships, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h2>{t('memberships.title')}</h2>
        {canCreate && (
          <Link to="/admin/memberships/create" className="btn btn--primary btn--md">
            {t('memberships.createNew')}
          </Link>
        )}
      </div>

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : memberships.length === 0 ? (
        <EmptyState
          title={t('memberships.emptyTitle')}
          description={t('memberships.emptyDescription')}
          action={canCreate ? { label: t('memberships.createNew'), href: '/admin/memberships/create' } : undefined}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredMemberships}
            columns={columns}
            editLink={canEdit ? (membership) => `/admin/memberships/${membership._id}/edit` : undefined}
            onDelete={canDelete ? handleDeleteClick : undefined}
          />
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('memberships.deleteTitle')}
        message={t('memberships.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default MembershipsListPage;
